import { useState, useRef, useEffect, useCallback } from 'react';
import { useSongs } from '@/context/SongContext';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Check, Loader2, Mic, MicOff, CircleDot } from 'lucide-react';
import { TUNING_PRESETS, findClosestNote } from '@/lib/utils';
import { toast } from 'sonner';

export default function GuitarTuner({ songId, onComplete }) {
    const { addFragment } = useSongs();
    const [isListening, setIsListening] = useState(false);
    const [selectedString, setSelectedString] = useState(null);
    const [selectedPreset, setSelectedPreset] = useState('Standard');
    const [detectedNote, setDetectedNote] = useState(null);
    const [saving, setSaving] = useState(false);
    const [permissionDenied, setPermissionDenied] = useState(false);
    
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const streamRef = useRef(null);
    const animationFrameRef = useRef(null);

    const currentStrings = TUNING_PRESETS[selectedPreset] || TUNING_PRESETS['Standard'];

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            stopListening();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const stopListening = useCallback(() => {
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
        }
        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
            audioContextRef.current.close();
        }
        audioContextRef.current = null;
        analyserRef.current = null;
        streamRef.current = null;
        setIsListening(false);
    }, []);

    const startListening = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            audioContextRef.current = audioContext;
            
            const analyser = audioContext.createAnalyser();
            analyser.fftSize = 4096;
            analyser.smoothingTimeConstant = 0.8;
            analyserRef.current = analyser;
            
            const source = audioContext.createMediaStreamSource(stream);
            source.connect(analyser);
            
            setIsListening(true);
            setPermissionDenied(false);
            
            // Start pitch detection
            detectPitch();
        } catch (error) {
            console.error('Error accessing microphone:', error);
            setPermissionDenied(true);
            toast.error('Microphone access denied. Please allow microphone access to use the tuner.');
        }
    };

    const detectPitch = () => {
        if (!analyserRef.current || !audioContextRef.current) return;
        
        const analyser = analyserRef.current;
        const sampleRate = audioContextRef.current.sampleRate;
        const bufferLength = analyser.fftSize;
        const dataArray = new Float32Array(bufferLength);
        
        const detect = () => {
            if (!analyserRef.current) return;
            
            analyser.getFloatTimeDomainData(dataArray);
            
            // Autocorrelation pitch detection
            const frequency = autoCorrelate(dataArray, sampleRate);
            
            if (frequency > 0) {
                const noteInfo = findClosestNote(frequency);
                if (noteInfo) {
                    setDetectedNote(noteInfo);
                }
            }
            
            animationFrameRef.current = requestAnimationFrame(detect);
        };
        
        detect();
    };

    // Autocorrelation algorithm for pitch detection
    const autoCorrelate = (buffer, sampleRate) => {
        const SIZE = buffer.length;
        const MAX_SAMPLES = Math.floor(SIZE / 2);
        let bestOffset = -1;
        let bestCorrelation = 0;
        let rms = 0;
        let foundGoodCorrelation = false;
        const correlations = new Array(MAX_SAMPLES);

        for (let i = 0; i < SIZE; i++) {
            const val = buffer[i];
            rms += val * val;
        }
        rms = Math.sqrt(rms / SIZE);
        
        // Not enough signal
        if (rms < 0.01) return -1;

        let lastCorrelation = 1;
        for (let offset = 0; offset < MAX_SAMPLES; offset++) {
            let correlation = 0;

            for (let i = 0; i < MAX_SAMPLES; i++) {
                correlation += Math.abs(buffer[i] - buffer[i + offset]);
            }
            correlation = 1 - correlation / MAX_SAMPLES;
            correlations[offset] = correlation;

            if (correlation > 0.9 && correlation > lastCorrelation) {
                foundGoodCorrelation = true;
                if (correlation > bestCorrelation) {
                    bestCorrelation = correlation;
                    bestOffset = offset;
                }
            } else if (foundGoodCorrelation) {
                const shift = (correlations[bestOffset + 1] - correlations[bestOffset - 1]) / correlations[bestOffset];
                return sampleRate / (bestOffset + 8 * shift);
            }
            lastCorrelation = correlation;
        }
        
        if (bestCorrelation > 0.01) {
            return sampleRate / bestOffset;
        }
        return -1;
    };

    const toggleListening = () => {
        if (isListening) {
            stopListening();
        } else {
            startListening();
        }
    };

    const handleSave = async () => {
        if (!detectedNote && !selectedString) {
            toast.error('Please detect a pitch or select a string first');
            return;
        }

        stopListening();
        setSaving(true);
        
        try {
            const targetString = selectedString 
                ? currentStrings.find(s => s.string === parseInt(selectedString))
                : null;
            
            const fragment = await addFragment(songId, {
                type: 'tuning',
                label: `Guitar Tuning (${selectedPreset})`,
                tuningPreset: selectedPreset,
                targetString: targetString?.label || null,
                targetNote: targetString?.note || null,
                targetFrequency: targetString?.frequency || null,
                detectedNote: detectedNote?.fullName || null,
                detectedFrequency: detectedNote?.detectedFrequency 
                    ? Math.round(detectedNote.detectedFrequency * 100) / 100 
                    : null,
                cents: detectedNote?.cents || null,
                inTune: detectedNote?.inTune || false
            });

            toast.success('Tuning saved');
            onComplete(fragment?.id);
        } catch (error) {
            console.error('Error saving tuning:', error);
            toast.error('Failed to save tuning');
        } finally {
            setSaving(false);
        }
    };

    // Calculate needle position (-50 to 50 cents = -90 to 90 degrees)
    const getNeedleRotation = () => {
        if (!detectedNote) return 0;
        const cents = Math.max(-50, Math.min(50, detectedNote.cents));
        return (cents / 50) * 45; // Map to -45 to 45 degrees
    };

    // Get accuracy indicator color
    const getAccuracyColor = () => {
        if (!detectedNote) return 'text-muted-foreground';
        const absCents = Math.abs(detectedNote.cents);
        if (absCents < 5) return 'text-success';
        if (absCents < 15) return 'text-warning';
        return 'text-destructive';
    };

    return (
        <div className="space-y-6 max-w-sm mx-auto">
            {/* Tuner Display */}
            <div className="flex flex-col items-center gap-4">
                {/* Tuner Circle */}
                <div className="relative w-48 h-48">
                    {/* Outer ring */}
                    <div className="absolute inset-0 rounded-full border-4 border-border bg-muted/30" />
                    
                    {/* Scale markers */}
                    <div className="absolute inset-4">
                        {/* Left marker (flat) */}
                        <div className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground font-medium">
                            ♭
                        </div>
                        {/* Center marker (in tune) */}
                        <div className="absolute left-1/2 top-2 -translate-x-1/2">
                            <div className="w-1 h-3 bg-success rounded-full" />
                        </div>
                        {/* Right marker (sharp) */}
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground font-medium">
                            ♯
                        </div>
                    </div>
                    
                    {/* Needle */}
                    <div 
                        className={`absolute left-1/2 top-1/2 w-1 h-20 -ml-0.5 origin-bottom transition-transform duration-150 ${isListening ? 'tuner-active' : ''}`}
                        style={{ 
                            transform: `translateY(-100%) rotate(${getNeedleRotation()}deg)`,
                        }}
                    >
                        <div 
                            className={`w-full h-full rounded-full ${getAccuracyColor()}`}
                            style={{ backgroundColor: 'currentColor' }}
                        />
                    </div>
                    
                    {/* Center dot */}
                    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                        <div className={`w-4 h-4 rounded-full ${isListening ? 'bg-primary' : 'bg-muted-foreground'}`} />
                    </div>
                    
                    {/* Note display */}
                    <div className="absolute inset-0 flex flex-col items-center justify-end pb-8">
                        <span className={`text-3xl font-display font-semibold ${getAccuracyColor()}`}>
                            {detectedNote?.fullName || '--'}
                        </span>
                        {detectedNote && (
                            <span className="text-sm text-muted-foreground">
                                {detectedNote.cents > 0 ? '+' : ''}{detectedNote.cents} cents
                            </span>
                        )}
                    </div>
                </div>

                {/* Frequency display */}
                {detectedNote && (
                    <div className="text-center animate-fade-in">
                        <span className="text-2xl font-display text-foreground tabular-nums">
                            {Math.round(detectedNote.detectedFrequency)} Hz
                        </span>
                    </div>
                )}

                {/* Listen button */}
                <Button
                    onClick={toggleListening}
                    disabled={permissionDenied}
                    variant={isListening ? 'destructive' : 'default'}
                    className={`w-32 h-12 rounded-full transition-all ${isListening ? 'animate-pulse' : ''}`}
                >
                    {isListening ? (
                        <><MicOff className="w-5 h-5 mr-2" /> Stop</>
                    ) : (
                        <><Mic className="w-5 h-5 mr-2" /> Listen</>
                    )}
                </Button>

                {/* Status text */}
                <p className="text-sm text-muted-foreground text-center">
                    {isListening 
                        ? 'Listening... play a note on your guitar'
                        : permissionDenied 
                            ? 'Microphone access required'
                            : 'Tap Listen and play a string'
                    }
                </p>
            </div>

            {/* String selector */}
            <div className="space-y-3 pt-4 border-t border-border">
                {/* Tuning preset selector */}
                <div className="space-y-2">
                    <Label>Tuning Preset</Label>
                    <Select value={selectedPreset} onValueChange={(v) => { setSelectedPreset(v); setSelectedString(null); }}>
                        <SelectTrigger className="hover:border-primary/50 transition-colors">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {Object.keys(TUNING_PRESETS).map((preset) => (
                                <SelectItem key={preset} value={preset}>{preset}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <Label>Target String</Label>
                <Select value={selectedString || 'auto'} onValueChange={(v) => setSelectedString(v === 'auto' ? null : v)}>
                    <SelectTrigger className="hover:border-primary/50 transition-colors">
                        <SelectValue placeholder="Select a string to tune" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="auto">Auto-detect</SelectItem>
                        {currentStrings.map((s) => (
                            <SelectItem key={s.string} value={String(s.string)}>
                                {s.label} ({s.note}) - {s.frequency} Hz
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                
                {/* Quick string buttons */}
                <div className="grid grid-cols-6 gap-2">
                    {currentStrings.map((s) => (
                        <button
                            key={s.string}
                            onClick={() => setSelectedString(String(s.string))}
                            className={`p-2 rounded-lg text-xs font-medium transition-all hover:scale-105 active:scale-95 ${
                                selectedString === String(s.string)
                                    ? 'bg-primary text-primary-foreground'
                                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                            }`}
                        >
                            {s.note.replace(/[0-9]/g, '')}
                        </button>
                    ))}
                </div>
            </div>

            {/* In-tune indicator */}
            {detectedNote?.inTune && (
                <div className="flex items-center justify-center gap-2 p-3 rounded-lg bg-success/10 text-success animate-fade-in">
                    <CircleDot className="w-5 h-5" />
                    <span className="font-medium">In Tune!</span>
                </div>
            )}

            {/* Save button */}
            <Button
                onClick={handleSave}
                disabled={saving || (!detectedNote && !selectedString)}
                className="w-full h-12 hover:scale-[1.02] active:scale-[0.98] transition-transform"
            >
                {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                    <Check className="w-4 h-4 mr-2" />
                )}
                Save Tuning
            </Button>
        </div>
    );
}